import { useTsRest } from '@/_shared/context/ts-rest.js';
import { Spinner, Tree, TreeItem, TreeItemLayout } from '@fluentui/react-components';
import { Document24Regular, Folder24Regular } from '@fluentui/react-icons';
import { useMemo } from 'react';
import { useStyles } from './styles.js';

type DocsNode = {
    name: string;
    path: string;
    type: 'file' | 'folder';
    children: DocsNode[];
};

type DocsTreeProps = {
    onSelect: (path: string) => void;
};

function collectAllPaths(nodes: DocsNode[]): string[] {
    const paths: string[] = [];
    for (const node of nodes) {
        paths.push(node.path);
        if (node.children.length > 0) {
            paths.push(...collectAllPaths(node.children));
        }
    }
    return paths;
}

function TreeNodes({
    nodes,
    onSelect,
}: {
    nodes: DocsNode[];
    onSelect: (path: string) => void;
}) {
    return (
        <>
            {nodes.map((node) => {
                const isFolder = node.type === 'folder';
                const icon = isFolder ? <Folder24Regular /> : <Document24Regular />;

                return (
                    <TreeItem key={node.path} itemType={isFolder ? 'branch' : 'leaf'} value={node.path}>
                        <TreeItemLayout
                            iconBefore={icon}
                            onClick={node.type === 'file' ? () => onSelect(node.path) : undefined}
                        >
                            {node.name}
                        </TreeItemLayout>
                        {isFolder && node.children.length > 0 && (
                            <Tree>
                                <TreeNodes nodes={node.children} onSelect={onSelect} />
                            </Tree>
                        )}
                    </TreeItem>
                );
            })}
        </>
    );
}

export function DocsTree({ onSelect }: DocsTreeProps) {
    const styles = useStyles();
    const tsr = useTsRest();

    const { data, isLoading } = tsr.getTree.useQuery({
        queryKey: ['docs', 'tree'],
    });

    const tree = data?.body?.tree ?? [];

    const allOpenPaths = useMemo(() => collectAllPaths(tree), [tree]);

    if (isLoading) {
        return (
            <div className={styles.container}>
                <Spinner label="Loading..." />
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2 className={styles.title}>Documentation</h2>
            </div>
            <div className={styles.treeContainer}>
                <Tree aria-label="Documentation tree" openItems={allOpenPaths}>
                    <TreeNodes nodes={tree} onSelect={onSelect} />
                </Tree>
            </div>
        </div>
    );
}
